import React, { useContext, useEffect, useState, ReactNode } from 'react'
import { Draggable, Droppable } from 'react-beautiful-dnd'
import RefreshDataContext, { RefreshChannelOptions } from 'shared/components/context/refresh-data'
import useAsyncEffect from 'shared/hooks/use-async-effect'
import { IChannel } from 'shared/types/channel'
import { Container, Item, ItemGif, ItemQuantityBox, ItemText, HoverIcons, PrivateOverlay } from './sc'
import { colors, getOpacity } from './utils'
import { LoaderListItem } from './channel-list-item'
import { CollectionContext } from 'shared/components/context/add-to-collections-manager'
import ModalContext from 'shared/components/context/modal'
import { getWebp } from 'shared/util/gif-extract'
import { CollectionStyleContext } from './../context/collection-style-context'

type ChannelListProps = {
    channel: IChannel
    isRoot: boolean
    addButton?: ReactNode
    onClick?: Function
}

const GIPHY_GIF =
    'https://media.giphy.com/media/Id66GDfKacJzxSvhqV/giphy.gif?cid=ecf05e477y344su9eci6tyt830qvgk85i4z6lxky2lcbqpv4&rid=giphy.gif&ct=g'

const ChannelList = ({ channel, addButton, onClick, isRoot }: ChannelListProps) => {
    const { itemWidth, selectedChannelIds } = useContext(CollectionStyleContext)
    const { isReordering, activeChannelColor, setActiveChannelColor, getChannelChildren, channels } = useContext(
        CollectionContext
    )
    const { openEditCollectionPanel } = useContext(ModalContext)
    const { onRefreshChannel } = useContext(RefreshDataContext)
    const [isLoading, setLoading] = useState(true)
    const [hoveringChannelEdit, setHoveringChannelEdit] = useState<number | undefined>()
    const [hoveringChannel, setHoveringChannel] = useState<number | undefined>()
    const [children, setChildren] = useState<IChannel[]>(channel.children || [])
    const fetchChildren = async channel => {
        const channelListChildren = await getChannelChildren(channel)
        setChildren(channelListChildren)
    }

    useAsyncEffect(async () => {
        if (channel.id !== 0) {
            setLoading(true)
            try {
                await fetchChildren(channel)
            } catch (error) {
                console.error(`Error fetching collection cabinet channel ${channel.id}`)
            }
        }
        setLoading(false)
    }, [channel.id])
    useEffect(() => {
        const off = onRefreshChannel((updatedChannel, options: RefreshChannelOptions) => {
            if (channel.id === updatedChannel.id) {
                if (updatedChannel.has_children) {
                    if (options.isReordering) {
                        setChildren(updatedChannel.children)
                    } else {
                        fetchChildren(updatedChannel)
                    }
                } else {
                    setChildren([])
                }
            }
        })
        return off
    }, [channel.id])

    if (isLoading) {
        return <LoaderListItem />
    }

    return (
        <>
            {channel.has_children ? (
                <Droppable droppableId={`${channel.id}`} isCombineEnabled>
                    {({ innerRef }) => (
                        <Container ref={innerRef}>
                            {addButton && addButton}
                            {children.map((channel, index) =>
                                channels[channel.id] ? (
                                    <Draggable
                                        key={channel.id}
                                        draggableId={channel.id.toString()}
                                        index={index}
                                        isDragDisabled={isReordering}
                                    >
                                        {provided => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.dragHandleProps}
                                                {...provided.draggableProps}
                                            >
                                                <Item
                                                    onClick={() => {
                                                        if (isRoot) {
                                                            setActiveChannelColor(colors[index % colors.length])
                                                        }

                                                        onClick?.(channel.id)
                                                    }}
                                                    itemWidth={itemWidth}
                                                    selected={selectedChannelIds?.includes(channel.id)}
                                                    gutter
                                                    darken={
                                                        !!(
                                                            // Check if its hovering edit pencil
                                                            (
                                                                hoveringChannelEdit === channel.id ||
                                                                // Check if its hovering another channel
                                                                (hoveringChannel && channel.id !== hoveringChannel)
                                                            )
                                                        )
                                                    }
                                                    onMouseEnter={() => {
                                                        setHoveringChannel(channel.id)
                                                    }}
                                                    onMouseLeave={() => {
                                                        setHoveringChannel(undefined)
                                                    }}
                                                >
                                                    {channel.has_children && (
                                                        <ItemQuantityBox>{channel.num_children || 1}</ItemQuantityBox>
                                                    )}
                                                    <ItemGif
                                                        src={getWebp(channel.featured_gif) || GIPHY_GIF}
                                                        alt={channel?.featured_gif?.title}
                                                    />
                                                    <ItemText
                                                        background={
                                                            !activeChannelColor || isRoot
                                                                ? colors[index % colors.length]
                                                                : activeChannelColor
                                                        }
                                                        opacity={isRoot ? 1 : getOpacity(index + 1)}
                                                    >
                                                        <a href={channel.url} target="_blank" rel="noreferrer">
                                                            {channel.display_name}
                                                        </a>
                                                    </ItemText>
                                                    {channel.is_private && (
                                                        <PrivateOverlay>
                                                            <span className="ss-lock" />
                                                        </PrivateOverlay>
                                                    )}
                                                    <HoverIcons>
                                                        <span
                                                            onMouseEnter={() => {
                                                                setHoveringChannelEdit(channel.id)
                                                            }}
                                                            onMouseLeave={() => {
                                                                setHoveringChannelEdit(undefined)
                                                            }}
                                                            className="ss-write"
                                                            onClick={e => {
                                                                e.stopPropagation()
                                                                openEditCollectionPanel(channel)
                                                            }}
                                                        />
                                                    </HoverIcons>
                                                </Item>
                                            </div>
                                        )}
                                    </Draggable>
                                ) : null
                            )}
                        </Container>
                    )}
                </Droppable>
            ) : (
                <Container>{addButton && addButton}</Container>
            )}
        </>
    )
}

export default ChannelList
